<template>
  <div>
    <vx-card>
      <heading size="h1" style-size="h4" class="mb-5">Contacting an Employer</heading>
      <p class="mb-5">
        When contacting an employer, you want to be clear that your intention is to learn and add
        value to their business. Now, at this stage, the menial things like sweeping floors or
        fetching coffee may be the level of “adding value” we are talking about. You have to be
        willing to add value where you can, in exchange for the employer taking the time to give you
        the “on the job training” and the “experience/exposure” that you are there to gain. It is
        called give-and-take and your enthusiasm is likely to be noticed.
      </p>

      <heading size="h2" style-size="h5" class="mb-5 text-bold"
        >Approaching Employers can be done:
      </heading>
      <ul class="default-list" role="list">
        <li role="listitem">Over the Phone</li>
        <li role="listitem">By email</li>
        <li role="listitem">Online via their website</li>
        <li role="listitem">In-person</li>
        <li role="listitem">Letter</li>
      </ul>

      <p class="mb-5">
        However, there will often be a combination of 2 of these. For example, an email with a
        follow-up phone call.
      </p>
      <p class="mb-5">No matter how you choose to contact an employer, keep these tips in mind:</p>

      <ul class="default-list">
        <li>
          Do your research. Find out some basic information about the company. This enables you to
          prepare and is likely to be interpreted as showing interest.
        </li>
        <li>
          If possible, know who you need to make the initial contact with. If it is a larger
          company, it may be a particular department like Human Resources.
        </li>
        <li>
          Know the dates you are available and be able to articulate what you would like to learn or
          experience. This can be quite general or simple.
        </li>
      </ul>
      <p class="mb-5">Let’s look at some tips for each of these approaches.</p>

      <heading size="h2" style-size="h5" class="mb-5 text-bold">Over the phone</heading>
      <p class="mb-5">When calling an employer:</p>

      <div class="vx-row mt-10">
        <div class="vx-col lg:w-3/4 md:w-1/2 w-full mb-base">
          <ul class="default-list">
            <li>
              Have a pen and paper handy just in case you need to make notes. You may also wish to
              have questions prewritten or even answers to questions you think you may get asked.
            </li>
            <li>
              Enquire whether there is a more convenient time for you to call, especially if they
              seem busy. But remember, don’t do things like call a restaurant at lunch time in the
              first place.
            </li>
            <li>Be polite, respectful, and cheerful even if you feel they aren’t.</li>
            <li>
              Practice your introduction and remember to speak clearly and slowly. It is natural to
              speak too fast when you are nervous. Obviously, no slang or swearing.
            </li>
            <li>
              Remember to use their name, or if you don’t know it, ask them, then remember it! If
              you need to contact them again, forgetting their name is not a great first impression.
            </li>
            <li>
              Make a connection in some way, if you can. For example, “I was given your details by
              my neighbour who used to work for your company” or, “I always enjoy shopping in your
              store and wondered if you offered work experience” or, “Journalism has always
              fascinated me and I loved your recent article in [publication name].”
            </li>
            <li>Be clear and to the point about why you are calling.</li>
            <li>If you don’t understand something, ask for clarification.</li>
            <li>Remember to thank them for their time at the end of the call.</li>
          </ul>
        </div>
        <div class="vx-col lg:w-1/4 md:w-1/2 w-full mb-base">
          <button
            aria-label="watch over the phone video"
            class="no-style"
            id="watch-phone-button"
            @click="openVideoPopup(videos['1'].url), `watch-phone-button`"
          >
            <img :src="videos['1'].image" class="img-responsive cursor-pointer" alt="" />
          </button>
        </div>
      </div>

      <heading size="h2" style-size="h5" class="mb-5 text-bold">In-Person</heading>
      <p class="mb-5">Everything mentioned in the phone tips plus:</p>
      <div class="vx-row mt-10">
        <div class="vx-col lg:w-3/4 md:w-1/2 w-full mb-base">
          <ul class="default-list" role="list">
            <li role="listitem">
              Be well presented. Wear neat, clean, and appropriate clothing. Be well groomed.
              Shower, clean teeth, hair and nails. Avoid excessive perfume/cologne, heavy make-up,
              and over-the-top accessories. It is OK to have your own style, but don’t over-do it.
            </li>
            <li role="listitem">
              Remember to make eye contact (without staring) and smile. Don’t slouch.
            </li>
            <li role="listitem">Turn off your mobile phone!</li>
            <li role="listitem">Be aware of the environment. Stay out of people’s way.</li>
          </ul>
        </div>
        <div class="vx-col lg:w-1/4 md:w-1/2 w-full mb-base">
          <button
            aria-label="watch in person video"
            class="no-style"
            id="watch-in-person-button"
            @click="openVideoPopup(videos['2'].url), `watch-in-person-button`"
          >
            <img :src="videos['2'].image" class="img-responsive cursor-pointer" alt="" />
          </button>
        </div>
      </div>

      <h5 class="mb-5 text-bold">Email or online</h5>

      <vs-tabs alignment="fixed" class="overwrite-tabs">
        <vs-tab
          :label="
            !isSmallerScreen
              ? 'Sample Email to Request a Work Experience Placement'
              : 'Sample Email'
          "
        >
          <div class="tab-text my-5">
            <p class="mb-1">To: (Email Address)</p>
            <p class="mb-5">Subject: Work Experience Placement Request</p>

            <p class="mb-1">Dear (Contact person, e.g., Mrs Mace,)</p>
            <p class="mb-5 highlight">(Know who you are writing to and address that person).</p>

            <p class="mb-1">
              My name is (Your Name) and I am a Year 10 student at (your school name). I wanted to
              enquire about the possibility of a work experience placement at Diamonds Jewellery
              Store.
            </p>
            <p class="mb-1 highlight">
              (Introduce yourself and where you go to school, and the purpose of the email).
            </p>
            <p class="mb-1">
              I have made some of my own jewellery and even sold some online. I love designing it,
              but also enjoy displaying it for people to view via my online images. I have a strong
              focus and a creative flare.
            </p>
            <p class="mb-1 highlight">(Mention some of your characteristics and skills)</p>
            <p class="mb-1">I am happy to provide a resume or answer any questions.</p>
            <p class="mb-1 highlight">
              (Let them know you are open to providing more information if required)
            </p>
            <p class="mb-1">
              Thank you for taking the time to consider my request for a work experience placement.
              I will follow up with a telephone call in a week’s time to find out if you are able to
              offer me a placement.
            </p>
            <p class="mb-5 highlight">
              (Thank them for their time and that you will follow-up with a phone call).
            </p>

            <p class="mb-1">Warm regards,</p>
            <p class="mb-1">(Your Name)</p>
            <p class="mb-1">(Your Email Address)</p>
            <p class="mb-5">(Your Phone Number)</p>
          </div>
        </vs-tab>
        <vs-tab
          :label="
            !isSmallerScreen
              ? 'Sample Letter to Request a Work Experience Placement'
              : 'Sample Letter'
          "
        >
          <div class="tab-text my-5">
            <p class="mb-1 text-right">(Your Name)</p>
            <p class="mb-1 text-right">(Your Street Address)</p>
            <p class="mb-1 text-right">(City, State, Postcode)</p>
            <p class="mb-1 text-right">(Your Phone Number)</p>
            <p class="mb-1 text-right">(Date: Day-Month-Year Format)</p>
            <p class="mb-5 text-right highlight">
              Make sure you give your contact details, including phone number.
            </p>

            <p class="mb-1">(Contact Person)</p>
            <p class="mb-1">(Title/Position)</p>
            <p class="mb-1">(Company Name)</p>
            <p class="mb-1">(Street Address)</p>
            <p class="mb-5">(City, State, Postcode)</p>

            <p class="mb-1">Dear (Contact person e.g., Mr Smith) or Dear Sir/Madam</p>
            <p class="mb-5 highlight">(Know who you are writing to and address that person).</p>

            <p class="mb-1">
              I am a Year 10 student at (your school name) and I am writing to enquire if your
              company would be able to offer me a work experience placement. I would like the
              opportunity of working in retail, particularly in the jewellery industry. I am
              interested in finding out more about the different types of work available.
            </p>
            <p class="mb-1 highlight">
              (Introduce yourself, where you go to school, and the purpose of the email).
            </p>
            <p class="mb-1">
              I have made some of my own jewellery and even sold some online. I love designing
              jewellery, but I also enjoy displaying it creatively, ready for people to view via my
              online images. I have a strong focus and an imaginative flare.
            </p>
            <p class="mb-1 hightlight">(Mention some of your characteristics and skills).</p>
            <p class="mb-1">I am happy to provide a resume or answer any questions you may have.</p>
            <p class="mb-1 hightlight">
              (Let them know you are open to providing more information if required)
            </p>
            <p class="mb-1">
              Thank you for taking the time to consider my request for Work Experience. I will
              follow-up with a telephone call in one week to find out if you are able to offer me a
              placement.
            </p>
            <p class="mb-5 hightlight">
              (Thank them for their time and inform them that you will follow-up with a phone call).
            </p>

            <p class="mb-1">Yours sincerely,</p>
            <p class="mb-1">(Sign your name - Leave enough space for your signature)</p>
            <p class="mb-5">(Print your name underneath)</p>
          </div>
        </vs-tab>
      </vs-tabs>
      <form id="form" class="mt-10" @submit.prevent="save">
        <div class="vx-row mb-6">
          <div class="vx-col w-full">
            <vs-checkbox v-model="data.confirmed" name="confirmed"
              >I have read all the information in this module
            </vs-checkbox>
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col w-full flex justify-between">
            <div>
              <button class="btn btn-primary btn-size-medium mr-3 mb-1">Submit</button>
              <router-link
                to="/student/workplace-preparation"
                class="btn btn-secondary btn-size-medium mb-1"
                >Back</router-link
              >
            </div>
          </div>
        </div>
      </form>
    </vx-card>
  </div>
</template>

<script>
import store from '@/store/store';

export default {
  computed: {
    data: {
      get: function () {
        return this.getModuleData();
      },
      set: function (d) {
        this.setModuleData(d);
      },
    },
    isSmallerScreen() {
      return this.$store.state.windowWidth < 768;
    },
    isConfirmed() {
      return this.$store.getters['authStudent/preworkWorkplacePreparationModules'][
        this.$route.params.id
      ];
    },
    prework() {
      return this.$store.getters['authStudent/prework'];
    },
  },
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/workplace_preparation/contact_employer_phone.jpg'),
          url: 'content/workplace_preparation/contact_by_phone.mp4',
        },
        2: {
          image: require('@/assets/images/workplace_preparation/contact_employer_in_person.jpg'),
          url: 'content/workplace_preparation/contact_in_person.mp4',
        },
      },
    };
  },
  methods: {
    save() {
      this.saveModule(this.data);
    },
    openVideoPopup(url, buttonId) {
      this.$store.dispatch(
        'popup/showVideoPopup',
        $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/' + url,
      );
      store.commit('popup/setFocusElemOnPopupClose', buttonId);
    },
  },
  watch: {
    'prework.workplace_preparation_modules'() {
      const modules = this.prework.workplace_preparation_modules;
      if (modules && modules[1]) {
        this.data = modules[1];
      }
    },
  },
  mounted() {
    if (this.isConfirmed) this.data.confirmed = true;
  },
  beforeDestroy() {
    this.data.confirmed = false;
  },
  inject: ['saveModule', 'getModuleData', 'setModuleData'],
};
</script>
<style scoped>
.highlight {
  color: #2e74b5;
}
</style>
